import { Box, CircularProgress, Typography } from "@mui/material";
import { useValidateDeskSession } from "./api/hooks";
import { useEffect } from "react";
import { allowedOrigins } from "../../common/auth/allowed-origins";
import { mainCont } from "./sxprops";

/**
 * Bizbox Desk SSO landing (isolated from `validate-product-session`).
 *
 * Mints a product-AGNOSTIC session via `/sso-desk` (facility roles across all products) and hands the
 * credentials back to Desk's callback in the `?c0A1&c0B2` shape its ValidatedPage consumes — so any
 * Beacon facility customer can use Desk, gated on "has a registered facility" not on the Desk product.
 *
 * Flow:
 *  • Signed into Beacon already → `/sso-desk` mints off the session cookie → redirect back to Desk.
 *  • Not signed in → `/sso-desk` errors → bounce through the shared `/login` (NO `aud`, so login mints
 *    an agnostic base session) with the callback pointing BACK here, then mint. A one-shot `deskRetry`
 *    guard prevents a login loop; if still unauthorized after login (no facility & no staff role) the
 *    user lands on the Accounts 403 page. The shared login + product-session flow are untouched.
 */
const ValidateDeskSession: React.FC = () => {
  const validateDeskSession = useValidateDeskSession();
  const params = new URLSearchParams(window.location.search);
  const callback = params.get("callback");

  const isValidCallback = (() => {
    try {
      const url = new URL(callback ?? "");
      return allowedOrigins.includes(url.origin);
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    // Session ready → forward the minted credentials to Desk's callback.
    if (validateDeskSession.status === "success" && callback) {
      if (!isValidCallback) {
        console.error("Blocked invalid callback URL:", callback);
        return;
      }
      const redirectUrl = new URL(callback);
      redirectUrl.searchParams.set("c0A1", validateDeskSession.data.c0A1);
      redirectUrl.searchParams.set("c0B2", validateDeskSession.data.c0B2);
      window.location.href = redirectUrl.toString();
      return;
    }

    // No Beacon session yet → log in once (no aud → agnostic mint), returning here to mint.
    if (validateDeskSession.status === "error") {
      const self = new URL(window.location.href);
      if (!self.searchParams.get("deskRetry")) {
        self.searchParams.set("deskRetry", "1");
        window.location.href =
          `${window.location.origin}/login` +
          `?callback=${encodeURIComponent(self.toString())}` +
          `&origin=${encodeURIComponent("/")}`;
      } else {
        // Logged in but still no valid Desk session (no facility & no staff role) → no access.
        window.location.href = `${window.location.origin}/error/403`;
      }
    }
  }, [
    callback,
    isValidCallback,
    validateDeskSession.data?.c0A1,
    validateDeskSession.data?.c0B2,
    validateDeskSession.status,
  ]);

  return (
    <Box sx={mainCont}>
      <CircularProgress
        sx={{
          color: "text.secondary",
        }}
      />

      <Typography color="text.secondary">
        {validateDeskSession.status === "pending"
          ? "Signing you in to Bizbox Desk"
          : validateDeskSession.status === "success"
          ? "Session ready!"
          : "Sign-in required"}
      </Typography>
    </Box>
  );
};

export default ValidateDeskSession;
